import { Link } from 'react-router-dom';
import SeoHead from '@/components/feature/SeoHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.discoveringmarche.com';

const PROGETTO_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Il Progetto — Discovering Marche | PR FSE+ 2021-2027 Regione Marche',
  description: 'Progetto Discovering Marche co-finanziato dall\'Unione Europea — PR FSE+ 2021/27 Regione Marche. Valorizzazione immobiliare dei borghi marchigiani.',
  url: `${SITE_URL}/progetto`,
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Il Progetto', item: `${SITE_URL}/progetto` },
    ],
  },
};

const ProgettoPage = () => {
  return (
    <main className="min-h-screen bg-ivory-50">
      <SeoHead
        title="Il Progetto — Discovering Marche | PR FSE+ 2021-2027 Regione Marche"
        description="Progetto Discovering Marche co-finanziato dall'Unione Europea — PR FSE+ 2021/27 Regione Marche. Valorizzazione dei borghi marchigiani con Ines Felicity Di Nallo."
        keywords="progetto Discovering Marche, FSE+ Regione Marche, valorizzazione borghi Marche, Ines Di Nallo Marche"
        canonical="/progetto"
        jsonLd={PROGETTO_JSONLD}
      />
      {/* Hero */}
      <section className="relative bg-anthracite-800 pt-36 pb-24 px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Marche%20Italy%20medieval%20borgo%20historic%20village%20stone%20architecture%20warm%20golden%20light%20aerial%20view%20rolling%20hills%20ancient%20buildings%20terracotta%20roofs%20beautiful%20Italian%20landscape%20serene%20atmosphere&width=1920&height=600&seq=progetto-hero01&orientation=landscape"
            alt="Borghi storici Marche"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-anthracite-800/70 via-anthracite-800/50 to-anthracite-800/90"></div>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">
            PR FSE+ 2021–2027 · Regione Marche
          </p>
          <h1 className="font-serif text-4xl md:text-5xl text-white font-light leading-tight mb-6">
            Il Progetto
          </h1>
          <p className="font-sans text-sm text-white/60 leading-relaxed max-w-2xl mx-auto">
            Progetto co-finanziato dall&apos;Unione Europea — PR FSE+ 2021/27 Regione Marche — DGR n. 977 del 24/06/2024
          </p>
        </div>
      </section>

      {/* Loghi istituzionali */}
      <section className="bg-white py-10 px-6 border-b border-stone-100">
        <div className="max-w-3xl mx-auto flex justify-center">
          <img
            src="https://storage.readdy-site.link/project_files/eb454c64-f721-4c2b-844b-992754102f07/6eeb93c6-733f-4c44-b4e0-cff1d3cfa5af_Immagine-2026-04-04-114006.png?v=bd284cbda24a565510a290857dc4e7e5"
            alt="Coesione Italia 2021-2027 — Cofinanziato dall'Unione Europea — Repubblica Italiana — Regione Marche"
            className="w-full max-w-2xl h-auto object-contain"
          />
        </div>
      </section>

      {/* La visione di Ines — testo lungo */}
      <section className="py-20 px-6 bg-ivory-50">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-xs tracking-widest uppercase text-terracotta-400 mb-4">La mia visione</p>
          <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light leading-snug mb-12">
            Fare dei borghi marchigiani<br />una destinazione riconosciuta<br />a livello internazionale.
          </h2>

          <div className="space-y-6 font-sans text-sm text-anthracite-500 leading-relaxed">
            <p>
              La mia visione nasce da un amore profondo per il territorio e da un percorso professionale che unisce competenza immobiliare, esperienza nel settore turistico e una solida conoscenza delle lingue inglese e spagnola. Oggi, grazie al sostegno dell&apos;Unione europea, questo progetto prende forma con un obiettivo chiaro e ambizioso: valorizzare il panorama immobiliare dei borghi marchigiani e renderlo accessibile, attrattivo e comprensibile a un pubblico italiano e internazionale.
            </p>
            <p>
              I borghi delle Marche non sono semplicemente luoghi in cui acquistare un immobile: sono custodi di storia, identità e bellezza autentica. Sono piazze silenziose baciate dalla luce dorata del tramonto, vicoli in pietra che raccontano secoli di tradizioni, colline che si aprono su panorami mozzafiato tra mare e montagna. Sono spazi in cui il tempo rallenta e la qualità della vita ritrova il suo significato più profondo.
            </p>
            <p>
              La mia visione è quella di trasformare ogni opportunità immobiliare in un racconto territoriale. Non proporre solo case, ma esperienze di vita. Ogni immobile viene contestualizzato nel suo borgo, nella sua comunità, nelle sue tradizioni enogastronomiche, nei suoi eventi culturali, nei suoi tesori artistici e paesaggistici. Chi sceglie un immobile nelle Marche sceglie la pace, la tranquillità, la sicurezza, ma anche la ricchezza di un patrimonio umano e culturale unico.
            </p>
            <p>
              Grazie alla mia esperienza nel turismo, comprendo profondamente cosa cerca chi arriva dall&apos;estero: autenticità, qualità della vita, bellezza, relazioni vere. Attraverso la padronanza dell&apos;inglese e dello spagnolo, costruisco ponti tra culture diverse, facilitando il dialogo e accompagnando ogni cliente in un percorso chiaro, trasparente e sereno verso l&apos;investimento o la realizzazione di un sogno di vita.
            </p>
            <p>
              Il finanziamento europeo rappresenta non solo un riconoscimento, ma una responsabilità: quella di contribuire alla rinascita e alla valorizzazione dei borghi marchigiani, contrastando lo spopolamento e generando nuove opportunità economiche e sociali. Il mio progetto intende creare una rete virtuosa tra proprietari, investitori, istituzioni locali e comunità, affinché ogni compravendita diventi un atto di valorizzazione del territorio.
            </p>
            <p>
              La mia visione è chiara: fare dei borghi marchigiani una destinazione immobiliare riconosciuta a livello internazionale, raccontandone l&apos;anima, proteggendone l&apos;identità e trasformandone le opportunità in futuro concreto. Perché ogni borgo ha una storia da custodire, e ogni casa può diventare l&apos;inizio di una nuova vita.
            </p>
          </div>

          {/* Firma */}
          <div className="mt-12 flex items-center gap-5 pt-8 border-t border-ivory-200">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-terracotta-50 border border-terracotta-200 shrink-0">
              <i className="ri-user-line text-terracotta-400 text-lg"></i>
            </div>
            <div>
              <p className="font-serif text-base text-anthracite-800">Ines Felicity Di Nallo</p>
              <p className="font-sans text-xs text-anthracite-400 mt-0.5">Agente Immobiliare Abilitata · CCIAA Ancona</p>
            </div>
          </div>
        </div>
      </section>

      {/* A chi mi rivolgo */}
      <section className="py-20 px-6 bg-white border-t border-stone-100">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-xs tracking-widest uppercase text-terracotta-400 mb-4">A chi mi rivolgo</p>
          <h2 className="font-serif text-3xl text-anthracite-800 font-light leading-snug mb-12">
            Ogni storia merita<br />il suo territorio.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-home-heart-line',
                title: 'Seconda casa e qualità di vita',
                desc: 'Chi desidera una seconda casa immersa nella quiete, o sogna di trasferirsi in Italia per uno stile di vita più equilibrato e autentico.',
              },
              {
                icon: 'ri-global-line',
                title: 'Acquirenti internazionali',
                desc: 'Chi arriva dall\'estero e cerca autenticità, bellezza e relazioni vere. Accompagnamento completo in italiano, inglese e spagnolo.',
              },
              {
                icon: 'ri-building-line',
                title: 'Investitori immobiliari',
                desc: 'Chi vuole investire in un territorio autentico e ancora ricco di potenziale: agriturismi, strutture ricettive, immobili di pregio.',
              },
            ].map((item) => (
              <div key={item.title} className="flex flex-col gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-terracotta-50 border border-terracotta-200">
                  <i className={`${item.icon} text-terracotta-400 text-base`}></i>
                </div>
                <h3 className="font-serif text-lg text-anthracite-800">{item.title}</h3>
                <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scheda tecnica progetto */}
      <section className="py-20 px-6 bg-ivory-50 border-t border-stone-100">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-xs tracking-widest uppercase text-terracotta-400 mb-4">Scheda tecnica</p>
          <h2 className="font-serif text-3xl text-anthracite-800 font-light leading-snug mb-12">
            Il progetto in sintesi.
          </h2>

          <div className="space-y-10">
            {/* Sintesi */}
            <div className="flex gap-6 items-start">
              <div className="shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-terracotta-50 border border-terracotta-200">
                <i className="ri-file-text-line text-terracotta-400 text-base"></i>
              </div>
              <div>
                <h3 className="font-serif text-xl text-anthracite-800 mb-3">Sintesi del Progetto</h3>
                <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                  Il progetto Marche Casa nasce nell&apos;ambito del Programma Regionale FSE+ 2021-2027 della Regione Marche, con l&apos;obiettivo di sostenere l&apos;inserimento lavorativo e l&apos;autoimprenditorialità nei settori ad alto potenziale economico, tra cui il mercato immobiliare e il turismo esperienziale nei borghi storici.
                </p>
              </div>
            </div>

            <div className="border-t border-ivory-200"></div>

            {/* Obiettivi */}
            <div className="flex gap-6 items-start">
              <div className="shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-terracotta-50 border border-terracotta-200">
                <i className="ri-focus-3-line text-terracotta-400 text-base"></i>
              </div>
              <div>
                <h3 className="font-serif text-xl text-anthracite-800 mb-3">Obiettivi</h3>
                <ul className="space-y-2">
                  {[
                    'Promuovere l\'accesso al lavoro autonomo in settori strategici per il territorio marchigiano.',
                    'Valorizzare il patrimonio immobiliare storico e rurale come leva di sviluppo economico sostenibile.',
                    'Favorire l\'integrazione tra turismo, cultura e mercato immobiliare nelle aree interne e costiere della regione.',
                    'Contrastare lo spopolamento dei borghi generando nuove opportunità economiche e sociali.',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 font-sans text-sm text-anthracite-500 leading-relaxed">
                      <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-terracotta-300"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="border-t border-ivory-200"></div>

            {/* Risultati */}
            <div className="flex gap-6 items-start">
              <div className="shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-terracotta-50 border border-terracotta-200">
                <i className="ri-bar-chart-line text-terracotta-400 text-base"></i>
              </div>
              <div>
                <h3 className="font-serif text-xl text-anthracite-800 mb-3">Risultati Attesi</h3>
                <ul className="space-y-2">
                  {[
                    'Creazione di un\'impresa individuale nel settore immobiliare specializzata in borghi storici marchigiani.',
                    'Sviluppo di un servizio trilingue (IT/EN/ES) per attrarre acquirenti nazionali e internazionali.',
                    'Contribuire alla rivitalizzazione dei borghi marchigiani attraverso la mediazione immobiliare qualificata.',
                    'Creazione di una rete virtuosa tra proprietari, investitori, istituzioni locali e comunità.',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 font-sans text-sm text-anthracite-500 leading-relaxed">
                      <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-terracotta-300"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer FSE + CTA */}
      <section className="bg-anthracite-800 py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-sans text-xs text-white/40 leading-relaxed mb-8">
            <strong className="text-white/60">Progetto co-finanziato dall&apos;Unione Europea</strong><br />
            PR FSE+ 2021/27 Regione Marche — DGR n. 977 del 24/06/2024
          </p>
          <h2 className="font-serif text-2xl md:text-3xl text-white font-light mb-4">
            Vuoi saperne di più?
          </h2>
          <p className="font-sans text-sm text-white/50 mb-8 leading-relaxed">
            Contattami per una consulenza gratuita. Ti accompagno in ogni fase, nella tua lingua.
          </p>
          <Link
            to="/contatti"
            className="inline-block font-sans text-xs tracking-widest uppercase px-8 py-3 bg-terracotta-300 text-white hover:bg-terracotta-400 transition-colors rounded-full cursor-pointer whitespace-nowrap"
          >
            Contattami
          </Link>
        </div>
      </section>

    </main>
  );
};

export default ProgettoPage;
